import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AGENCY_LINK_ROLE_LABELS, formatToLocal, withDirectionParticle, withSubjectParticle } from '@safetyhub/shared';
import { Badge, Button, Card, ConfirmModal, buttonClassName } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { usePermissions } from '../../hooks/usePermissions';
import { apiErrorMessage } from '../../lib/api';
import { agencySiteLinkApi, agencySiteLinkQueryKeys } from '../../lib/agencySiteLink';
import { AGENCY_LINK_REQUESTS_PATH, REQUEST_STATUS_LABEL, requestSiteLabel } from '../../lib/agencySiteLinkView';

type PendingAction = 'approve' | 'reject' | 'cancel' | null;

/**
 * 대행 연결 요청 한 건(P4-1 · 알림 `SITE_AGENCY_LINK_REQUESTED`·`…_RESOLVED`의 목적지).
 * - 업체(incoming) OWNER: 승인 · 거절 — 초안 요청이면 승인 순간 업체 소유 현장이 생긴다(P4-3)
 * - 기관(outgoing) `site.manage`: 취소
 * 대기 아닌 요청은 결과만 보인다(만료는 없다 — P4-4). 실패는 토스트로 받고 확인 모달을 닫아 막다른 길을 만들지 않는다.
 */
export default function AgencyLinkRequestDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const organizationId = useAuthStore((s) => s.currentMembership?.companyId);
  const { isOwner, canManageSites } = usePermissions();
  const { showToast } = useToastStore();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingAction>(null);

  const queryKey = agencySiteLinkQueryKeys.request(organizationId ?? '', requestId ?? '');
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => agencySiteLinkApi.getRequest(organizationId!, requestId!),
    enabled: !!organizationId && !!requestId,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: async (action: Exclude<PendingAction, null>) => {
      if (action === 'approve') return agencySiteLinkApi.approve(organizationId!, requestId!);
      if (action === 'reject') return agencySiteLinkApi.reject(organizationId!, requestId!);
      return agencySiteLinkApi.cancel(organizationId!, requestId!);
    },
    onSuccess: (_result, action) => {
      setPending(null);
      showToast(
        action === 'approve' ? '요청을 승인했습니다.' : action === 'reject' ? '요청을 거절했습니다.' : '요청을 취소했습니다.',
        'success',
      );
      void queryClient.invalidateQueries({ queryKey: agencySiteLinkQueryKeys.all });
      if (action === 'approve') void queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (err) => {
      setPending(null);
      showToast(apiErrorMessage(err) ?? '요청을 처리하지 못했습니다.', 'error');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12" role="status" aria-label="불러오는 중">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="mx-auto max-w-xl">
        <Card padding="xl" radius="lg" elevation="md">
          <p className="text-sm text-rose-600">{apiErrorMessage(error) ?? '연결 요청을 찾을 수 없습니다.'}</p>
          <Link to={AGENCY_LINK_REQUESTS_PATH} className={`${buttonClassName('secondary')} mt-4`}>
            요청 목록으로
          </Link>
        </Card>
      </div>
    );
  }

  const isIncoming = data.direction === 'incoming';
  const isPending = data.status === 'PENDING';
  const canResolve = isIncoming && isPending && isOwner;
  const canCancel = !isIncoming && isPending && canManageSites;
  const busy = mutation.isPending;

  const confirmCopy: Record<Exclude<PendingAction, null>, { title: string; body: string; label: string; danger: boolean }> = {
    approve: {
      title: '연결 요청을 승인할까요?',
      body: data.siteDraft
        ? `승인하면 우리 조직 소유로 「${data.siteDraft.name}」 현장이 만들어지고 ${withSubjectParticle(data.agency.name)} ${withDirectionParticle(AGENCY_LINK_ROLE_LABELS[data.role])} 연결됩니다.`
        : `승인하면 ${withSubjectParticle(data.agency.name)} ${withDirectionParticle(AGENCY_LINK_ROLE_LABELS[data.role])} 이 현장에 연결되어 법정 서류를 작성할 수 있습니다. 연결은 현장 상세에서 언제든 해제할 수 있습니다.`,
      label: '승인',
      danger: false,
    },
    reject: { title: '연결 요청을 거절할까요?', body: '거절하면 기관에 알림이 전달됩니다.', label: '거절', danger: true },
    cancel: { title: '연결 요청을 취소할까요?', body: '취소한 요청은 되돌릴 수 없습니다. 필요하면 다시 요청해주세요.', label: '요청 취소', danger: true },
  };
  const confirm = pending ? confirmCopy[pending] : null;

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <Link to={AGENCY_LINK_REQUESTS_PATH} className="text-sm text-ink-600 hover:text-ink-900">
        ← 요청 목록으로
      </Link>
      <Card padding="xl" radius="lg" elevation="md">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-ink-900">대행 연결 요청</h1>
          <Badge tone={isPending ? 'amber' : data.status === 'APPROVED' ? 'emerald' : 'slate'} size="sm">
            {REQUEST_STATUS_LABEL[data.status]}
          </Badge>
        </div>
        <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-ink-500">현장</dt>
            <dd className="text-ink-900">
              {data.site && data.status === 'APPROVED' ? (
                <Link to={`/sites/${data.site.siteId}`} className="text-brand-600 hover:underline">
                  {requestSiteLabel(data)}
                </Link>
              ) : (
                requestSiteLabel(data)
              )}
              {data.site?.district && <span className="ml-1 text-ink-500">({data.site.district})</span>}
              {!data.site && data.siteDraft?.address && <span className="ml-1 text-ink-500">({data.siteDraft.address})</span>}
            </dd>
          </div>
          <div>
            <dt className="text-ink-500">대행 역할</dt>
            <dd className="text-ink-900">{AGENCY_LINK_ROLE_LABELS[data.role]}</dd>
          </div>
          <div>
            <dt className="text-ink-500">요청 기관</dt>
            <dd className="text-ink-900">{data.agency.name}</dd>
          </div>
          <div>
            <dt className="text-ink-500">업체</dt>
            <dd className="text-ink-900">{data.target.name}</dd>
          </div>
          <div>
            <dt className="text-ink-500">요청</dt>
            <dd className="text-ink-900">
              {data.requestedByName ?? '-'} · {formatToLocal(new Date(data.requestedAt))}
            </dd>
          </div>
          {data.resolvedAt && (
            <div>
              <dt className="text-ink-500">처리</dt>
              <dd className="text-ink-900">
                {data.resolvedByName ?? '-'} · {formatToLocal(new Date(data.resolvedAt))}
              </dd>
            </div>
          )}
        </dl>

        {isIncoming && isPending && !isOwner && (
          <p className="mt-4 text-sm text-ink-500">조직 소유자만 승인하거나 거절할 수 있습니다.</p>
        )}

        {(canResolve || canCancel) && (
          <div className="mt-6 flex justify-end gap-2">
            {canResolve && (
              <>
                <Button variant="secondary" onClick={() => setPending('reject')} disabled={busy}>
                  거절
                </Button>
                <Button variant="primary" onClick={() => setPending('approve')} disabled={busy}>
                  승인
                </Button>
              </>
            )}
            {canCancel && (
              <Button variant="secondary" onClick={() => setPending('cancel')} disabled={busy}>
                요청 취소
              </Button>
            )}
          </div>
        )}
      </Card>

      {confirm && pending && (
        <ConfirmModal
          open
          title={confirm.title}
          body={confirm.body}
          confirmLabel={confirm.label}
          danger={confirm.danger}
          busy={busy}
          onConfirm={() => mutation.mutate(pending)}
          onClose={() => {
            if (!busy) setPending(null);
          }}
        />
      )}
    </div>
  );
}
